/**
 * Rational frame time. Zod-free so the runtime, the harness page and the exporter can import it without pulling in
 * the schema. A frame rate is a ratio num/den (25/1, 50/1, 30000/1001); frame n starts at n*1000*den/num ms.
 *
 * Every timestamp is computed directly from the frame number with one division, never by accumulating a frame
 * duration, so there is no drift: the integer product n*1000*den stays exact well past 2^40 (hours of 4K60).
 */

export interface FrameRate {
  num: number;
  den: number;
}

/** The start time of frame `n` in milliseconds: n*1000*den/num, computed with a single rounding. */
export function frameToMs(n: number, rate: FrameRate): number {
  return (n * 1000 * rate.den) / rate.num;
}

/** The frame that contains time `ms` (floor), robust to the float error of frameToMs. */
export function msToFrame(ms: number, rate: FrameRate): number {
  const exact = (ms * rate.num) / (1000 * rate.den);
  const nearest = Math.round(exact);
  // A timestamp produced by frameToMs may come back as 41.99999999 frames; snap within a micro-frame.
  if (Math.abs(exact - nearest) < 1e-6) return nearest;
  return Math.floor(exact);
}

/** Snap a time to the start of the nearest frame. */
export function snapToFrame(ms: number, rate: FrameRate): number {
  return frameToMs(Math.round((ms * rate.num) / (1000 * rate.den)), rate);
}

/** Frames per second as a float, for display and for OGraf `frameRate` constraints (e.g. 29.97002997). */
export function fps(rate: FrameRate): number {
  return rate.num / rate.den;
}

/** Whether a rate is NTSC-style (30000/1001, 60000/1001): its timecode drops frame numbers. */
export function isDropFrame(rate: FrameRate): boolean {
  return rate.den === 1001 && (rate.num === 30000 || rate.num === 60000);
}

/**
 * SMPTE timecode of frame `n`: HH:MM:SS:FF, or HH:MM:SS;FF with drop-frame numbering at 29.97 and 59.94 fps
 * (frame numbers 0 and 1, or 0 to 3, are skipped at the start of every minute except every tenth).
 */
export function formatTimecode(n: number, rate: FrameRate): string {
  const base = Math.round(rate.num / rate.den);
  let f = Math.max(0, Math.floor(n));
  const drop = isDropFrame(rate);
  if (drop) {
    const d = base === 60 ? 4 : 2;
    const per10 = Math.round((rate.num / rate.den) * 600);
    const perMin = base * 60 - d;
    const tens = Math.floor(f / per10);
    const rem = f % per10;
    f += d * 9 * tens + (rem > d ? d * Math.floor((rem - d) / perMin) : 0);
  }
  const ff = f % base;
  const s = Math.floor(f / base);
  const two = (v: number) => String(v).padStart(2, '0');
  return `${two(Math.floor(s / 3600))}:${two(Math.floor(s / 60) % 60)}:${two(s % 60)}${drop ? ';' : ':'}${two(ff)}`;
}
